// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { BaseDialog, UploadCloud01Icon } from '@metarouter-private/mr-mui';
import { useQueryPipelines, useTrackWithUser } from '@ion/api';
import useQueryConsentCategories from '@ion/api/consent-categories/useQueryConsentCategories';

import { useAnalyticsFilesTableContext } from 'app/analytics-files/AnalyticsFilesPage/AnalyticsFilesTable/AnalyticsFilesTableContext';
import useBulkUploadState from './useBulkUploadState';
import FileSelector from './FileSelector';
import UploadProgress from './UploadProgress';
import UploadResults from './UploadResults';
import ModalActions from './ModalActions';
import generateErrorReportYaml from './generateErrorReportYaml';

export default function BulkUploadModal({ open, onClose }) {
  const { data: pipelines, loading: pipelinesLoading } = useQueryPipelines();
  const { data: consentCategories, loading: consentCategoriesLoading } = useQueryConsentCategories();
  const { refetch } = useAnalyticsFilesTableContext();
  const state = useBulkUploadState({ pipelines, consentCategories });
  const [trackBulkUpload] = useTrackWithUser('bulk_analytics_files_uploaded');

  useEffect(() => {
    if (!open) {
      state.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    const shouldRefresh = state.isComplete && state.results.createdFiles.length > 0;

    if (state.isComplete && state.results.createdFiles.length > 0) {
      trackBulkUpload();
    }

    onClose();

    if (shouldRefresh && refetch) {
      refetch();
    }
  };

  const handleDownloadReport = () => {
    const yaml = generateErrorReportYaml(state.results.createdFiles, state.results.failedFiles);
    const blob = new Blob([yaml], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    link.download = `bulk-upload-report-${timestamp}.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderContent = () => {
    if (state.isComplete) {
      return (
        <UploadResults total={state.fileCount} succeeded={state.results.succeeded} failed={state.results.failed} />
      );
    }

    if (state.isProcessing) {
      return <UploadProgress fileCount={state.fileCount} />;
    }

    return (
      <FileSelector
        selectedFile={state.selectedFile}
        fileCount={state.fileCount}
        onFileSelect={state.handleFileSelect}
        hasError={state.hasError}
        pipelinesLoading={pipelinesLoading}
        hasPipelines={pipelines.length > 0}
      />
    );
  };

  const canUpload =
    !!state.selectedFile && !state.hasError && !pipelinesLoading && !consentCategoriesLoading && pipelines.length > 0;

  const dialogProps = {
    open,
    maxWidth: 'sm',
    fullWidth: true,
    disableEscapeKeyDown: state.isProcessing,
    onClose: state.isProcessing ? undefined : handleClose,
  };

  return (
    <BaseDialog
      DialogProps={dialogProps}
      title="Bulk Upload Analytics Files"
      TitleIcon={UploadCloud01Icon}
      content={renderContent()}
      actions={
        <ModalActions
          isIdle={state.isIdle}
          isProcessing={state.isProcessing}
          isComplete={state.isComplete}
          canUpload={canUpload}
          onCancel={handleClose}
          onUpload={state.handleUpload}
          onClose={handleClose}
          onDownloadReport={handleDownloadReport}
        />
      }
    />
  );
}

BulkUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
